// ============================================================================
// EduPath Sprint 5 - Conflict Resolution Modal
// ============================================================================
// Modal dialog for resolving sync conflicts and server rejections.
// Provides clear explanations and actionable options for each rejection type.
// ============================================================================

import React from 'react';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import type { OfflineQueueItem, RejectCode } from '@/types/offline';

// ─── Rejection Explanations ──────────────────────────────────────────────────

interface RejectionInfo {
  title: string;
  description: string;
  icon: string;
  canRetry: boolean;
  recommendation: string;
}

const rejectionInfo: Record<RejectCode | 'default', RejectionInfo> = {
  RESPONSE_COMPLETENESS: {
    title: 'Incomplete Assessment',
    description:
      'This assessment does not meet the minimum 80% completion threshold. The system requires at least 80% of questions to be answered before submission.',
    icon: '📋',
    canRetry: false,
    recommendation:
      'This submission cannot be recovered. The student will need to retake the assessment and complete at least 80% of the questions.',
  },
  SESSION_FRESHNESS: {
    title: 'Session Expired',
    description:
      'This assessment session is more than 7 days old. Offline submissions must be synced within 7 days of completion to maintain data integrity.',
    icon: '⏰',
    canRetry: false,
    recommendation:
      'This submission is too old to process. The student will need to start a new assessment session.',
  },
  DUPLICATE_SUBMISSION: {
    title: 'Already Submitted',
    description:
      'This assessment was already successfully submitted. This can happen if the sync ran in the background while you were offline.',
    icon: '✅',
    canRetry: false,
    recommendation:
      'No action needed — the assessment has already been recorded. You can safely discard this copy.',
  },
  SESSION_INVALID: {
    title: 'Invalid Session',
    description:
      'The assessment session could not be found on the server. It may have been deleted or the session ID is incorrect.',
    icon: '❌',
    canRetry: false,
    recommendation:
      'This submission cannot be processed. The student may need to start a new assessment.',
  },
  VALIDATION_ERROR: {
    title: 'Validation Failed',
    description:
      'The server could not validate this submission. This may be a temporary issue.',
    icon: '⚠️',
    canRetry: true,
    recommendation:
      'Try syncing again. If the problem persists, the assessment may need to be retaken.',
  },
  default: {
    title: 'Sync Issue',
    description:
      'There was a problem syncing this assessment submission.',
    icon: '⚠️',
    canRetry: true,
    recommendation:
      'Try syncing again. If the problem persists, contact support.',
  },
};

function getInfo(item: OfflineQueueItem): RejectionInfo {
  if (item.rejectCode && item.rejectCode in rejectionInfo) {
    return rejectionInfo[item.rejectCode];
  }
  return rejectionInfo.default;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const modalStyles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
    animation: 'ep-modal-fade 0.2s ease-out',
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    maxWidth: '480px',
    width: '100%',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'ep-modal-slide 0.2s ease-out',
  },
  header: {
    padding: '24px 24px 0',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  body: {
    padding: '16px 24px',
  },
  section: {
    marginBottom: '16px',
  },
  label: {
    fontSize: '11px',
    fontWeight: 600 as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: '#6b7280',
    marginBottom: '6px',
  },
  value: {
    fontSize: '14px',
    color: '#1f2937',
    lineHeight: 1.5,
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    background: '#f9fafb',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px',
  },
  detailItem: {
    fontSize: '12px',
  },
  detailLabel: {
    color: '#6b7280',
    marginBottom: '2px',
  },
  detailValue: {
    color: '#1f2937',
    fontWeight: 500 as const,
  },
  flagList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
    marginTop: '8px',
  },
  flagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500 as const,
    background: '#fef3c7',
    color: '#92400e',
  },
  recommendation: {
    background: '#eff6ff',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '13px',
    color: '#1e40af',
    lineHeight: 1.5,
    marginBottom: '16px',
    borderLeft: '3px solid #3b82f6',
  },
  footer: {
    padding: '16px 24px 24px',
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    borderTop: '1px solid #e5e7eb',
    marginTop: '8px',
  },
  btn: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '13px',
    fontWeight: 500 as const,
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  },
  closeBtn: {
    position: 'absolute' as const,
    top: '16px',
    right: '16px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    color: '#9ca3af',
    fontSize: '18px',
    lineHeight: 1,
  },
  errorBox: {
    background: '#fef2f2',
    borderRadius: '6px',
    padding: '10px 12px',
    fontSize: '12px',
    color: '#991b1b',
    marginBottom: '16px',
    fontFamily: 'monospace',
    wordBreak: 'break-all' as const,
  },
};

// ─── Flag Labels ─────────────────────────────────────────────────────────────

const flagLabels: Record<string, string> = {
  SHORT_DURATION: 'Completed too quickly',
  STRAIGHT_LINING: 'Same answer pattern detected',
  RAPID_RESPONSES: 'Very fast response times',
  IMPOSSIBLE_TIMING: 'Timing inconsistency',
};

// ─── Component ───────────────────────────────────────────────────────────────

export const ConflictResolutionModal: React.FC = () => {
  const { conflictModalOpen, conflictItem, resolveConflict, closeConflictModal } =
    useOfflineSync();

  if (!conflictModalOpen || !conflictItem) return null;

  const info = getInfo(conflictItem);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-KE', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const completionPct = conflictItem.sessionMeta.questionCount > 0
    ? Math.round(
        (conflictItem.sessionMeta.answeredCount /
          conflictItem.sessionMeta.questionCount) *
          100
      )
    : 0;

  return (
    <>
      <style>{`
        @keyframes ep-modal-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes ep-modal-slide {
          from { opacity: 0; transform: translateY(10px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .ep-modal-btn:hover { filter: brightness(0.92); }
      `}</style>

      <div
        style={modalStyles.overlay}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeConflictModal();
        }}
      >
        <div style={{ ...modalStyles.modal, position: 'relative' }}>
          {/* Close button */}
          <button
            style={modalStyles.closeBtn}
            onClick={closeConflictModal}
            aria-label="Close"
          >
            ×
          </button>

          {/* Header */}
          <div style={modalStyles.header}>
            <div style={modalStyles.headerTitle}>
              <span style={{ fontSize: '24px' }}>{info.icon}</span>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>
                  {info.title}
                </h3>
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: '13px',
                    color: '#6b7280',
                  }}
                >
                  Session {conflictItem.sessionId.slice(0, 8)}…
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div style={modalStyles.body}>
            {/* Description */}
            <p style={{ ...modalStyles.value, marginBottom: '16px' }}>
              {info.description}
            </p>

            {/* Assessment details */}
            <div style={modalStyles.detailGrid}>
              <div style={modalStyles.detailItem}>
                <div style={modalStyles.detailLabel}>Assessment</div>
                <div style={modalStyles.detailValue}>
                  {conflictItem.assessmentType}
                </div>
              </div>
              <div style={modalStyles.detailItem}>
                <div style={modalStyles.detailLabel}>Completion</div>
                <div style={modalStyles.detailValue}>
                  {conflictItem.sessionMeta.answeredCount}/
                  {conflictItem.sessionMeta.questionCount} ({completionPct}%)
                </div>
              </div>
              <div style={modalStyles.detailItem}>
                <div style={modalStyles.detailLabel}>Completed</div>
                <div style={modalStyles.detailValue}>
                  {formatDate(conflictItem.sessionMeta.completedAt)}
                </div>
              </div>
              <div style={modalStyles.detailItem}>
                <div style={modalStyles.detailLabel}>Queued</div>
                <div style={modalStyles.detailValue}>
                  {formatDate(conflictItem.queuedAt)}
                </div>
              </div>
              <div style={modalStyles.detailItem}>
                <div style={modalStyles.detailLabel}>Attempts</div>
                <div style={modalStyles.detailValue}>
                  {conflictItem.retryCount}/{conflictItem.maxRetries}
                </div>
              </div>
              <div style={modalStyles.detailItem}>
                <div style={modalStyles.detailLabel}>Learner</div>
                <div style={modalStyles.detailValue}>
                  {conflictItem.learnerId.slice(0, 12)}…
                </div>
              </div>
            </div>

            {/* Integrity flags */}
            {conflictItem.integrityFlags.length > 0 && (
              <div style={modalStyles.section}>
                <div style={modalStyles.label}>Integrity Flags</div>
                <div style={modalStyles.flagList}>
                  {conflictItem.integrityFlags.map((flag) => (
                    <span key={flag} style={modalStyles.flagChip}>
                      {flagLabels[flag] || flag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Error message */}
            {conflictItem.errorMessage && (
              <div style={modalStyles.errorBox}>
                {conflictItem.errorMessage}
              </div>
            )}

            {/* Recommendation */}
            <div style={modalStyles.recommendation}>
              <strong>Recommendation:</strong> {info.recommendation}
            </div>
          </div>

          {/* Footer / Actions */}
          <div style={modalStyles.footer}>
            {info.canRetry && (
              <button
                className="ep-modal-btn"
                style={{
                  ...modalStyles.btn,
                  background: '#3b82f6',
                  color: '#fff',
                }}
                onClick={() =>
                  resolveConflict(conflictItem.id, 'RETRY')
                }
              >
                Try Again
              </button>
            )}
            <button
              className="ep-modal-btn"
              style={{
                ...modalStyles.btn,
                background: '#f3f4f6',
                color: '#374151',
              }}
              onClick={() =>
                resolveConflict(conflictItem.id, 'SAVE_LOCAL')
              }
            >
              Keep Locally
            </button>
            <button
              className="ep-modal-btn"
              style={{
                ...modalStyles.btn,
                background: '#fef2f2',
                color: '#dc2626',
              }}
              onClick={() =>
                resolveConflict(conflictItem.id, 'DISCARD')
              }
            >
              Discard
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConflictResolutionModal;
